import React, { useState, useEffect } from 'react'
import styles from './CompanyForm.module.css'
import { useContent } from '../../content/ContentProvider'

// Country codes are the stored values; only the names come from the catalogue.
const countries = (t) => [
  ['AT', t('companies.form.country.at')], ['BE', t('companies.form.country.be')],
  ['BG', t('companies.form.country.bg')], ['CH', t('companies.form.country.ch')],
  ['CZ', t('companies.form.country.cz')], ['DE', t('companies.form.country.de')],
  ['DK', t('companies.form.country.dk')], ['ES', t('companies.form.country.es')],
  ['FI', t('companies.form.country.fi')], ['FR', t('companies.form.country.fr')],
  ['GB', t('companies.form.country.gb')], ['GR', t('companies.form.country.gr')],
  ['HR', t('companies.form.country.hr')], ['HU', t('companies.form.country.hu')],
  ['IT', t('companies.form.country.it')], ['LU', t('companies.form.country.lu')],
  ['NL', t('companies.form.country.nl')], ['NO', t('companies.form.country.no')],
  ['PL', t('companies.form.country.pl')], ['PT', t('companies.form.country.pt')],
  ['RO', t('companies.form.country.ro')], ['SE', t('companies.form.country.se')],
  ['SI', t('companies.form.country.si')], ['SK', t('companies.form.country.sk')],
  ['TR', t('companies.form.country.tr')],
]

export function CompanyForm({ company, onSubmit, isLoading }) {
  const { t } = useContent()
  const COUNTRIES = countries(t)
  const [formData, setFormData] = useState({
    companyId: '',
    name: '',
    rics: '',
    country: 'PL',
    description: '',
  })

  // Map backend Company entity → form state when editing existing company
  useEffect(() => {
    if (company) {
      setFormData({
        companyId: company.companyId || '',
        name: company.name || '',
        rics: company.rics || '',
        country: company.country || 'PL',
        description: company.description || '',
      })
    } else {
      // Reset for "New company"
      setFormData({
        companyId: '',
        name: '',
        rics: '',
        country: 'PL',
        description: '',
      })
    }
  }, [company])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // Preserve `id` if editing existing record
    const payload = company?.id ? { ...formData, id: company.id } : formData
    onSubmit(payload)
  }

  const isEdit = !!company?.id

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.field}>
        <label>{t('companies.form.companyId.label')}</label>
        <input
          type="text"
          name="companyId"
          value={formData.companyId}
          onChange={handleChange}
          placeholder={t('companies.form.companyId.placeholder')}
          required
          disabled={isLoading || isEdit}
        />
        {isEdit && <small className={styles.hint}>{t('companies.form.companyId.hint')}</small>}
      </div>

      <div className={styles.field}>
        <label>{t('companies.form.name.label')}</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t('companies.form.name.placeholder')}
          required
          disabled={isLoading}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label>{t('companies.form.rics.label')}</label>
          <input
            type="text"
            name="rics"
            value={formData.rics}
            onChange={handleChange}
            placeholder={t('companies.form.rics.placeholder')}
            required
            disabled={isLoading}
          />
        </div>
        <div className={styles.field}>
          <label>{t('companies.form.country.label')}</label>
          <select
            name="country"
            value={formData.country}
            onChange={handleChange}
            required
            disabled={isLoading}
          >
            {COUNTRIES.map(([code, label]) => (
              <option key={code} value={code}>
                {code} — {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.field}>
        <label>{t('companies.form.description.label')}</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder={t('companies.form.description.placeholder')}
          rows={3}
          disabled={isLoading}
        />
      </div>

      <div className={styles.actions}>
        <button type="submit" disabled={isLoading} className={styles.submitBtn}>
          {isLoading ? t('companies.form.saving') : isEdit ? t('companies.form.save') : t('companies.form.create')}
        </button>
      </div>
    </form>
  )
}
